import { useState } from 'react'
import { Download, GitBranch } from 'lucide-react'
import {
  Badge,
  Button,
  IconButton,
  LoadingState,
  Modal,
  StatusBadge,
} from '@/components/ui'
import { templateDownloadUrl, useTemplateDiff, useTemplateVersions } from '../api/templatesApi.ts'
import type { ContractTemplate } from '../types.ts'

interface Props {
  template: ContractTemplate | null
  open: boolean
  onOpenChange: (o: boolean) => void
}

export function TemplateVersionHistoryModal({ template, open, onOpenChange }: Props) {
  const [diffBase, setDiffBase] = useState<string | null>(null)

  const { data: versions = [], isLoading } = useTemplateVersions(template?.id ?? null)
  const latestId = versions.length > 0 ? versions[versions.length - 1]!.id : null
  const compareId = diffBase ?? (versions.length > 1 ? versions[versions.length - 2]!.id : null)

  const { data: diff, isLoading: diffLoading } = useTemplateDiff(
    latestId,
    compareId && compareId !== latestId ? compareId : null,
  )

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={`Versionsverlauf: ${template?.name ?? ''}`}
      footer={<Button variant="secondary" onClick={() => onOpenChange(false)}>Schließen</Button>}
    >
      <div className="space-y-6">
        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            <div className="space-y-2">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
                >
                  <span className="font-mono text-sm font-semibold w-10">v{v.version}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm truncate">{v.originalFileName ?? v.name}</span>
                      {v.isMaterialChange && (
                        <Badge variant="warning" size="sm">Wesentliche Änderung</Badge>
                      )}
                      {v.active && <StatusBadge status="active" />}
                    </div>
                    <p className="text-caption text-gray-500 mt-0.5">
                      {new Date(v.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {' · '}
                      {((v.fileSize ?? 0) / 1024).toFixed(0)} KB
                      {' · '}
                      {(v.placeholders as string[] ?? []).length} Platzhalter
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!v.active && v.id !== latestId && (
                      <Button
                        variant="ghost"
                        size="xs"
                        leadingIcon={<GitBranch className="w-3.5 h-3.5" />}
                        onClick={() => setDiffBase(v.id)}
                        aria-pressed={diffBase === v.id}
                      >
                        Diff
                      </Button>
                    )}
                    <IconButton
                      icon={<Download className="w-4 h-4" />}
                      aria-label="Herunterladen"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(templateDownloadUrl(v.id), '_blank', 'noopener')}
                    />
                  </div>
                </div>
              ))}
            </div>

            {diff && (
              <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-800">
                  Diff v{diff.b.version} → v{diff.a.version}
                </h4>
                {diff.diff.added.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-success-700 mb-1">+ Hinzugefügt</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.added.map((p) => (
                        <Badge key={p} variant="success" size="sm">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {diff.diff.removed.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-danger-700 mb-1">− Entfernt</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.removed.map((p) => (
                        <Badge key={p} variant="danger" size="sm">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {diff.diff.unchanged.length > 0 && (
                  <div>
                    <p className="text-caption font-medium text-gray-500 mb-1">Unverändert</p>
                    <div className="flex flex-wrap gap-1">
                      {diff.diff.unchanged.map((p) => (
                        <Badge key={p} variant="neutral" size="sm">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {diffLoading && <LoadingState />}
                {diff.diff.added.length === 0 && diff.diff.removed.length === 0 && !diffLoading && (
                  <p className="text-sm text-gray-500">Keine Platzhalter-Unterschiede.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
